import { useMemo } from "react";
import { featuredManga } from "@/mocks/mangaData";

interface FeaturedProps {
  query: string;
}

export default function Featured({ query }: FeaturedProps) {
  const list = useMemo(() => {
    if (!query) return featuredManga;
    const k = query.toLowerCase();
    return featuredManga.filter(
      (m) =>
        m.title.toLowerCase().includes(k) ||
        m.genre.toLowerCase().includes(k) ||
        m.tags.some((t) => t.toLowerCase().includes(k)) ||
        m.author.toLowerCase().includes(k),
    );
  }, [query]);

  return (
    <section id="recommend" className="py-12 md:py-20 bg-background-50">
      <div className="mx-auto max-w-7xl px-4 md:px-6">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8 md:mb-12">
          <div>
            <span className="inline-flex items-center gap-1.5 text-primary-600 text-sm font-medium">
              <i className="ri-fire-fill"></i>
              本命推荐精选
            </span>
            <h2 className="mt-2 font-display text-2xl md:text-4xl text-foreground-950">
              当本命的入坑作
            </h2>
            <p className="mt-2 text-sm md:text-base text-foreground-600 max-w-xl">
              每一部都是按本命标准筛出来的，画风、剧情、人物全都在线，放心入坑不踩雷。
            </p>
          </div>
          <span className="inline-flex items-center gap-1.5 text-xs md:text-sm text-foreground-500 bg-background-200/60 px-3 py-1.5 rounded-full whitespace-nowrap">
            <i className="ri-search-line"></i>
            {query ? `正在为你筛选：“${query}”` : "共 6 部精挑本命"}
          </span>
        </div>

        {list.length === 0 ? (
          <div className="text-center py-16 bg-background-100/60 rounded-2xl">
            <i className="ri-emotion-sad-line text-4xl text-foreground-400"></i>
            <p className="mt-3 text-foreground-600">暂未找到匹配的本命漫，换个关键词试试吧</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 md:gap-5">
            {list.map((m) => (
              <article
                key={m.id}
                className="group flex flex-col sm:flex-row gap-4 p-4 md:p-5 bg-background-100/70 border border-background-200/70 rounded-xl hover:bg-background-50 hover:border-primary-200 transition-colors"
              >
                <div className="w-full sm:w-36 md:w-40 shrink-0">
                  <div className="relative rounded-lg overflow-hidden aspect-[3/4] bg-background-200">
                    <img
                      src={m.cover}
                      alt={`${m.title}本命推荐漫画封面`}
                      title={`${m.title} - 131漫画网本命推荐`}
                      className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                    <span className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-accent-500 text-background-50 text-xs whitespace-nowrap">
                      {m.rating}分
                    </span>
                  </div>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <h3 className="font-display text-lg md:text-xl text-foreground-950">
                      {m.title}
                    </h3>
                    <span className="text-xs text-foreground-500 whitespace-nowrap mt-1">{m.status}</span>
                  </div>
                  <p className="mt-1 text-xs md:text-sm text-foreground-500">
                    {m.author} · {m.genre}
                  </p>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    {m.tags.map((t) => (
                      <span
                        key={t}
                        className="px-2 py-0.5 rounded-full bg-secondary-100 text-secondary-900 text-xs whitespace-nowrap"
                      >
                        {t}
                      </span>
                    ))}
                  </div>
                  <p className="mt-3 text-xs md:text-sm text-foreground-700 leading-relaxed line-clamp-2">
                    {m.desc}
                  </p>
                  <p className="mt-2 text-xs md:text-sm text-primary-700 leading-relaxed">
                    <i className="ri-heart-3-fill text-accent-500 mr-1"></i>
                    {m.reason}
                  </p>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}